// Platform admin (/admin) — reachable only to users whose group maps to an
// admin/owner IAM role (gated by the route). Provisioning is self-service (any
// signed-in platform member creates a project directly via /app/new — no
// approval queue), so this page is the project REGISTRY: list every project and
// delete one. Delete enqueues `delete-project` (privileged platform handler).

import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconRefresh, IconTrash } from "@tabler/icons-react";
import { isSessionExpiredError } from "/sdk/index.js";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { DeleteResult, ProjectManifest } from "../../lib/contracts.ts";
import { useSession } from "../../session/SessionProvider.tsx";

// The SessionExpiredModal is the real affordance; this just keeps the raw
// `cognito-identity ... 400 ... Token expired` string out of the list's
// error box in the brief window before the modal opens.
function listErrorMessage(e: unknown): string {
  if (isSessionExpiredError(e)) return "Session expired — sign in again to reload this list.";
  return e instanceof Error ? e.message : String(e);
}

const TIMEOUT_MS = 5 * 60 * 1000;
const POLL_MS = 1000;

function fmtTime(t?: string): string {
  return (t ?? "").slice(0, 19).replace("T", " ");
}

export function AdminHome() {
  const { client, me } = useSession();

  const [manifests, setManifests] = useState<ProjectManifest[]>([]);
  const [manifestsLoading, setManifestsLoading] = useState(false);
  const [manifestsError, setManifestsError] = useState<string | null>(null);

  // Delete flow: a per-slug status map, plus a confirm modal that requires
  // re-typing the slug (it's destructive — schedules the KMS key for deletion
  // and empties the bucket).
  type DeleteStatus = "running" | "done" | "err";
  // OWNERS COME FROM THE BROKER, NOT THE MANIFEST.
  //
  // The manifest used to carry `ownerUsername` — the owner's EMAIL — and it
  // lives in the member-readable registry prefix (s3db/shared/). On the console that
  // means every s3p user could read the email of every other customer. So the
  // manifest no longer carries it, and this page asks `manage-operators` instead:
  // that broker verifies the caller's token and only answers admins. Same column,
  // sourced from the place that actually checks who's asking.
  const [owners, setOwners] = useState<Record<string, string>>({});
  const [delStatus, setDelStatus] = useState<Record<string, DeleteStatus>>({});
  const [delResult, setDelResult] = useState<Record<string, string>>({});

  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch when the project list changes.
  useEffect(() => {
    if (!client || manifests.length === 0) return;
    let cancelled = false;
    void (async () => {
      const operators = client.operators;
      if (!operators) return;
      const found: Record<string, string> = {};
      await Promise.all(
        manifests.map(async (m) => {
          try {
            // The SDK's operators module, pointed at each registry slug — the
            // broker still verifies the caller server-side.
            const r = await operators.list({ slug: m.slug });
            const owner = r.find((o) => o.role === "owner");
            if (owner) found[m.slug] = owner.email;
          } catch {
            /* not an operator of this one, or the broker said no — leave it blank */
          }
        }),
      );
      if (!cancelled) setOwners(found);
    })();
    return () => {
      cancelled = true;
    };
  }, [client, manifests.map((m) => m.slug).join(",")]);
  const [confirmTarget, setConfirmTarget] = useState<ProjectManifest | null>(null);
  const [confirmText, setConfirmText] = useState("");

  const refreshManifests = useCallback(async () => {
    if (!client) return;
    setManifestsLoading(true);
    setManifestsError(null);
    try {
      const Projects = client.collection<ProjectManifest>("projects", {
        filename: "manifest.json",
      });
      const list = await Projects.list();
      list.sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));
      setManifests(list);
    } catch (e) {
      setManifests([]);
      setManifestsError(listErrorMessage(e));
    } finally {
      setManifestsLoading(false);
    }
  }, [client]);

  const runDelete = useCallback(
    async (slug: string) => {
      if (!client || !me) return;
      setConfirmTarget(null);
      setConfirmText("");
      setDelStatus((p) => ({ ...p, [slug]: "running" }));
      setDelResult((p) => ({ ...p, [slug]: "" }));
      try {
        if (!client.projects) throw new Error("project lifecycle unavailable on this client");
        // No id_token in the input: the SDK ships it in the Lambda payload and the
        // runtime verifies it into ctx.caller. Putting it here would copy a live
        // token into the inflight record, which its owner can read.
        const res: DeleteResult = await client.projects.delete(slug, {
          timeoutMs: TIMEOUT_MS,
          pollIntervalMs: POLL_MS,
        });
        setDelStatus((p) => ({ ...p, [slug]: "done" }));
        setDelResult((p) => ({ ...p, [slug]: JSON.stringify(res, null, 2) }));
        setTimeout(() => void refreshManifests(), 800);
      } catch (e) {
        setDelStatus((p) => ({ ...p, [slug]: "err" }));
        setDelResult((p) => ({ ...p, [slug]: e instanceof Error ? e.message : String(e) }));
      }
    },
    [client, me, refreshManifests],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: run once when client is ready
  useEffect(() => {
    if (!client) return;
    void refreshManifests();
  }, [client]);

  return (
    <Stack gap="xl">
      <Group justify="space-between" align="flex-end">
        <Stack gap={2}>
          <Title order={1}>Platform admin</Title>
          <Text c="dimmed" size="sm">
            Review the project registry. Provisioning is self-service — anyone
            creates a project from <Code>/signup</Code>. Signed in as{" "}
            {me?.username ?? me?.sub} · {me?.role ?? "(no role)"}.
          </Text>
          {/* The admin sub-pages had nothing linking to them — they were
              URL-only, discoverable by knowing the path. */}
          <Group gap="xs" mt="xs">
            <Button component={Link} to="/admin/fleet" size="xs" variant="default">
              Fleet — usage &amp; plan
            </Button>
            <Button component={Link} to="/admin/observability" size="xs" variant="default">
              Observability
            </Button>
          </Group>
        </Stack>
      </Group>

      {/* All projects */}
      <Card withBorder radius="md" padding="lg">
        <Group justify="space-between" mb="sm">
          <Title order={3}>All registered projects</Title>
          <Button
            size="xs"
            variant="default"
            leftSection={<IconRefresh size={14} />}
            loading={manifestsLoading}
            onClick={() => void refreshManifests()}
          >
            Refresh
          </Button>
        </Group>
        {manifestsError ? (
          <Alert color="red" variant="light" title="List failed">
            {manifestsError}
          </Alert>
        ) : manifests.length === 0 && !manifestsLoading ? (
          <Text c="dimmed" size="sm" fs="italic">
            No projects yet.
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={560}>
            <Table verticalSpacing="sm" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Slug</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Owner</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {manifests.map((m) => {
                  const ds = delStatus[m.slug];
                  return (
                    <Table.Tr key={m.slug}>
                      <Table.Td>
                        <Code>{m.slug}</Code>
                      </Table.Td>
                      <Table.Td>{m.name}</Table.Td>
                      <Table.Td c="dimmed">
                        {owners[m.slug] ?? (
                          <Text size="sm" c="dimmed" component="span">
                            {m.ownerSub?.slice(0, 8)}…
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td c="dimmed">{fmtTime(m.createdAt)}</Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        {ds === "running" ? (
                          <Badge color="orange" variant="light">
                            deleting…
                          </Badge>
                        ) : ds === "done" ? (
                          <Badge color="gray" variant="light">
                            ✓ deleted
                          </Badge>
                        ) : (
                          <Button
                            size="xs"
                            color="red"
                            variant="light"
                            leftSection={<IconTrash size={14} />}
                            onClick={() => {
                              setConfirmText("");
                              setConfirmTarget(m);
                            }}
                          >
                            {ds === "err" ? "Retry delete" : "Delete"}
                          </Button>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}

        {/* Surface delete errors (success rows disappear on refresh). */}
        {Object.entries(delStatus)
          .filter(([, s]) => s === "err")
          .map(([slug]) => (
            <Alert key={slug} mt="sm" color="red" variant="light" title={`Delete failed — ${slug}`}>
              <Code block style={{ maxHeight: 180, overflow: "auto" }}>
                {delResult[slug]}
              </Code>
            </Alert>
          ))}
      </Card>

      {/* Type-to-confirm destructive delete. */}
      <Modal
        opened={confirmTarget !== null}
        onClose={() => setConfirmTarget(null)}
        title={<Text fw={600}>Delete project</Text>}
        centered
      >
        {confirmTarget && (
          <Stack gap="md">
            <Text size="sm">
              This permanently tears down everything for <Code>{confirmTarget.slug}</Code>: its S3
              bucket and all data, Cognito user + identity pools, IAM roles, and schedules its KMS
              key for deletion. <Text span fw={600}>This cannot be undone.</Text>
            </Text>
            <TextInput
              label={
                <Text size="sm">
                  Type <Code>{confirmTarget.slug}</Code> to confirm
                </Text>
              }
              value={confirmText}
              onChange={(e) => setConfirmText(e.currentTarget.value)}
              placeholder={confirmTarget.slug}
              autoFocus
              data-autofocus
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setConfirmTarget(null)}>
                Cancel
              </Button>
              <Button
                color="red"
                leftSection={<IconTrash size={14} />}
                disabled={confirmText !== confirmTarget.slug}
                onClick={() => void runDelete(confirmTarget.slug)}
              >
                Delete {confirmTarget.slug}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
